import React, { useEffect, useState } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useForm } from 'react-hook-form';
import { Edit3 } from 'lucide-react';

import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';
import { getContrastTextColor } from '@HELPERS/colorUtils';

import { AdminVendor, useUpdateVendor } from '@SERVICES';

import BaseModal from '@MODALS/BaseModal';
import BasicInputValidatable from '@COMPONENTS/COMMON/inputs/input/BasicInput/BasicInputValidatable.component';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import HiddenFormButton from '@COMPONENTS/COMMON/buttons/HiddenFormButton';

import styles from './EditVendorModal.module.scss';

const cx: CX = classnames.bind(styles);

interface EditVendorFormValues {
    name: string;
    company: string;
    contactPerson: string;
    address: string;
    email: string;
    phone: string;
    website: string;
    status: 'active' | 'inactive';
    contractStatus: 'active' | 'inactive';
    notes: string;
    isVisible: boolean;
}

interface EditVendorModalProps {
    vendor: AdminVendor;
    isOpen: boolean;
    onClose: () => void;
}

function EditVendorModal({ vendor, isOpen, onClose }: EditVendorModalProps) {
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        reset,
    } = useForm<EditVendorFormValues>({
        defaultValues: {
            name: vendor.name,
            company: vendor.company,
            contactPerson: (vendor as any).contactPerson || '',
            address: vendor.address,
            email: vendor.email,
            phone: vendor.phone,
            website: (vendor as any).website || '',
            status: (vendor as any).status || 'active',
            contractStatus: (vendor as any).contractStatus || 'active',
            notes: (vendor as any).notes || '',
            isVisible: vendor.isVisible,
        },
    });

    const [
        updateVendor,
        {
            isLoading, isSuccess, isError, error,
        },
    ] = useUpdateVendor();

    const formButtonRef = React.useRef<HTMLButtonElement>(null);
    const isVisible = watch('isVisible');
    const [iconStrokeColor, setIconStrokeColor] = useState('#ffffff');

    useEffect(() => {
        if (isOpen) {
            reset({
                name: vendor.name,
                company: vendor.company,
                contactPerson: (vendor as any).contactPerson || '',
                address: vendor.address,
                email: vendor.email,
                phone: vendor.phone,
                website: (vendor as any).website || '',
                status: (vendor as any).status || 'active',
                contractStatus: (vendor as any).contractStatus || 'active',
                notes: (vendor as any).notes || '',
                isVisible: vendor.isVisible,
            });
        }
    }, [isOpen, vendor, reset]);

    useEffect(() => {
        if (isSuccess) {
            notify.success('Vendor has been updated successfully', {
                toastId: 'update-vendor-success',
            });
            onClose();
        }
    }, [isSuccess, onClose]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'update-vendor-permission-error',
                });
            } else {
                notify.error('Failed to update vendor. Please try again.', {
                    toastId: 'update-vendor-error',
                });
            }
        }
    }, [isError, error]);

    const onSave = (formValues: EditVendorFormValues) => {
        updateVendor({
            id: vendor.id,
            name: formValues.name,
            company: formValues.company,
            address: formValues.address,
            email: formValues.email,
            phone: formValues.phone,
            isVisible: formValues.isVisible,
            contactPerson: formValues.contactPerson,
            website: formValues.website,
            status: formValues.status,
            contractStatus: formValues.contractStatus,
            notes: formValues.notes,
        });
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    // Update icon color based on primary color changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const updateIconColor = () => {
                const primaryColor = getComputedStyle(document.documentElement)
                    .getPropertyValue('--marriott-primary')
                    .trim() || '#00a4ba';
                
                // Determine appropriate stroke color for the icon
                const strokeColor = getContrastTextColor(primaryColor);
                setIconStrokeColor(strokeColor);
            };

            // Update immediately
            updateIconColor();

            // Listen for CSS variable changes (if app settings change)
            const observer = new MutationObserver(updateIconColor);
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['style']
            });

            return () => observer.disconnect();
        }
    }, []);

    if (!isOpen) return null;

    return (
        <BaseModal onClose={handleClose}>
            <div className={cx('edit-vendor-modal')}>
                <div className={cx('modal-header')}>
                    <div className={cx('icon-wrapper')}>
                        <Edit3 
                            className={cx('modal-icon')} 
                            size={24}
                            strokeWidth={2}
                            stroke={iconStrokeColor}
                        />
                    </div>
                    <div className={cx('header-content')}>
                        <h2 className={cx('title')}>Edit Vendor</h2>
                        <p className={cx('subtitle')}>Update vendor information</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSave)} className={cx('form')}>
                    <div className={cx('form-section')}>
                        <div className={cx('form-row')}>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<EditVendorFormValues>
                                    label="Vendor Name"
                                    placeholder="Enter vendor name"
                                    validation={{
                                        name: 'name' as const,
                                        control,
                                        rules: {
                                            required: 'Vendor name is required',
                                            minLength: {
                                                value: 2,
                                                message: 'Name must be at least 2 characters',
                                            },
                                        },
                                    }}
                                />
                            </div>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<EditVendorFormValues>
                                    label="Company"
                                    placeholder="Enter company name"
                                    validation={{
                                        name: 'company' as const,
                                        control,
                                        rules: {
                                            required: 'Company name is required',
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<EditVendorFormValues>
                                    label="Contact Person"
                                    placeholder="Enter contact person name"
                                    validation={{
                                        name: 'contactPerson' as const,
                                        control,
                                    }}
                                />
                            </div>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<EditVendorFormValues>
                                    label="Website"
                                    placeholder="Enter website URL"
                                    validation={{
                                        name: 'website' as const,
                                        control,
                                        rules: {
                                            pattern: {
                                                value: /^(https?:\/\/)?([\w-])+\.{1}([a-zA-Z]{2,63})([/\w\-._~:?#[\]@!$&'()*+,;=%]*)?$/,
                                                message: 'Please enter a valid website URL',
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<EditVendorFormValues>
                                    label="Email"
                                    placeholder="Enter email address"
                                    validation={{
                                        name: 'email' as const,
                                        control,
                                        rules: {
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address',
                                            },
                                        },
                                    }}
                                />
                            </div>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<EditVendorFormValues>
                                    label="Phone"
                                    placeholder="Enter phone number"
                                    validation={{
                                        name: 'phone' as const,
                                        control,
                                    }}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group')}>
                                <label htmlFor="status" className={cx('form-label')}>Status</label>
                                <select
                                    id="status"
                                    {...register('status')}
                                    className={cx('form-select')}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div className={cx('form-group')}>
                                <label htmlFor="contractStatus" className={cx('form-label')}>Contract Status</label>
                                <select
                                    id="contractStatus"
                                    {...register('contractStatus')}
                                    className={cx('form-select')}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group', 'full-width')}>
                                <BasicInputValidatable<EditVendorFormValues>
                                    label="Address"
                                    placeholder="Enter vendor address"
                                    validation={{
                                        name: 'address' as const,
                                        control,
                                    }}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group', 'full-width')}>
                                <label htmlFor="notes" className={cx('form-label')}>Notes</label>
                                <textarea
                                    id="notes"
                                    {...register('notes')}
                                    placeholder="Enter any additional notes about this vendor..."
                                    className={cx('form-textarea')}
                                    rows={3}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group', 'visibility-group')}>
                                <label className={cx('visibility-label')}>
                                    <input
                                        type="checkbox"
                                        checked={isVisible}
                                        onChange={(e) => setValue('isVisible', e.target.checked)}
                                        className={cx('visibility-checkbox')}
                                    />
                                    <span className={cx('checkbox-indicator')} />
                                    <span className={cx('checkbox-text')}>
                                        Make vendor visible in selection lists
                                    </span>
                                </label>
                                <p className={cx('visibility-help')}>
                                    When checked, this vendor will be available for selection in documents and sections.
                                </p>
                            </div>
                        </div>
                    </div>

                    <HiddenFormButton ref={formButtonRef} />
                </form>

                <div className={cx('modal-actions')}>
                    <BasicButton
                        variant="neutral"
                        size="medium"
                        title="Cancel"
                        onClick={handleClose}
                        locked={isLoading}
                        style={{ flex: 1 }}
                    />
                    <BasicButton
                        variant="primary"
                        size="medium"
                        title={isLoading ? 'Updating...' : 'Update Vendor'}
                        isProcessing={isLoading}
                        onClick={() => {
                            formButtonRef.current?.click();
                        }}
                        style={{ flex: 2 }}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

export default EditVendorModal; 